import type { UsageStage } from '@ni/shared';
import type { AdminUsage } from '../api/types';
import { formatMoney } from './format';

/**
 * The month's spend, broken down for the operator panel (server-design §9,
 * 2026-09-03).
 *
 * The labels live here rather than in the markup for the same reason the money
 * formatter does: what a stage is *called* is copy, and copy is worth having one
 * place and one test for. They are written as end results — what the spend
 * bought — because the operator reading this panel is watching cost, not tracing
 * a graph.
 *
 * **An unknown key is labelled by its key, never dropped.** A row written under a
 * vocabulary this build has not heard of is still real money, and a breakdown
 * that silently omitted it would no longer add up to the total above it.
 *
 * Typed `Record<UsageStage, string>` — the **one** thing this app takes from
 * `@ni/shared`, and a type-only import, so nothing reaches the bundle. It is here
 * because the fallback above is a runtime safety net and not a licence: a stage
 * added to the vocabulary with no label would otherwise ship as a raw snake_case
 * key on the operator's page and nothing would have said so. Now it fails
 * typecheck.
 */
export const STAGE_LABELS: Record<UsageStage, string> = {
  quick_search_harvest: 'Quick search · web harvest',
  quick_search_merge: 'Quick search · candidate merge',
  simple_summary: 'Quick search · first read',
  facet_retrieve: 'Research · retrieval',
  facet_extract: 'Research · extraction',
  orchestrator: 'Round planning',
  analysis: 'Written analysis',
  report_inputs: 'Report · reading the description',
  report_collect: 'Report · collecting sources',
  report_write: 'Report · writing',
};

/**
 * What a row with no stage is called. It is two kinds of row at once — spend
 * reported by an engine that did not break it down, and the server's own calls
 * (intake extraction, resolution), which are attributed by source instead — and
 * the panel says the true thing about both rather than guessing which it is.
 */
export const NO_STAGE_LABEL = 'Not broken down';

/**
 * `stage` is a plain string here, not a `UsageStage`: it comes off a database row
 * the server did not re-validate, so a build meeting a value it has never heard of
 * is a real case and the raw key is the honest label for it.
 */
export const stageLabel = (stage: string | null): string =>
  stage === null ? NO_STAGE_LABEL : ((STAGE_LABELS as Record<string, string>)[stage] ?? stage);

export interface BreakdownRow {
  readonly key: string;
  readonly label: string;
  readonly amount: string;
}

/** The by-stage lines, labelled and formatted. Empty when there is nothing to show. */
export function stageRows(usage: AdminUsage): BreakdownRow[] {
  return (usage.breakdown?.by_stage ?? []).map((line) => ({
    key: line.stage ?? '',
    label: stageLabel(line.stage),
    amount: formatMoney(line.micro_usd),
  }));
}

/**
 * **What kind of work the spend was**, in end-result words.
 *
 * The keys are `ai_usage_event.source`. `pipeline` is every call a research run
 * makes, which is the money in an ordinary month; `import` is the note reading of
 * a file somebody brought in, which is the money in the month a club arrives, and
 * the reason this table exists at all — it used to be invisible inside the
 * by-stage table's "not broken down" line.
 */
export const SOURCE_LABELS: Record<string, string> = {
  pipeline: 'Research',
  intake: 'Notes read on a record',
  resolution: 'Identity matching',
  import: 'Imported files',
  merge_triage: 'Duplicate questions checked',
  external: 'Search and social providers',
};

/** The by-source lines. An unknown key is labelled by its key, never dropped. */
export function sourceRows(usage: AdminUsage): BreakdownRow[] {
  return (usage.breakdown?.by_source ?? []).map((line) => ({
    key: line.source,
    label: SOURCE_LABELS[line.source] ?? line.source,
    amount: formatMoney(line.micro_usd),
  }));
}

/** The by-model lines. Model ids are shown verbatim — the concrete version is the point. */
export function modelRows(usage: AdminUsage): BreakdownRow[] {
  return (usage.breakdown?.by_model ?? []).map((line) => ({
    key: line.model_id,
    label: line.model_id,
    amount: formatMoney(line.micro_usd),
  }));
}

/* ---- the Claude connector's calls (2026-09-07) ---- */

export const CONNECTOR_HEADING = 'Claude connector calls';

/**
 * **The caption exists to stop a reader looking for a number that is not there.**
 *
 * Every other table on this page is money. This one is not, and an operator who
 * has just read three spend tables will assume the fourth is a fourth spend
 * table with the amounts missing. So the caption says whose bill the thinking
 * was on before the columns get a chance to mislead.
 *
 * It says *organization*, not *club*: this is the operator's surface, where the
 * rename of 2026-09-03 applies and the reader is the one person never inside a
 * vertical. (The MCP note's §2.6 wrote "the club's Claude plan"; that is the
 * note's voice, not this page's, and the sweep in `copy.spec.tsx` is the rule.)
 */
export const CONNECTOR_CAPTION =
  'Model cost is on the organization’s Claude plan; these are calls into the system.';

/** Column headings, here rather than in the markup for the same reason the labels are. */
export const CONNECTOR_TOOL_HEADING = 'Tool';
export const CONNECTOR_USER_HEADING = 'Who asked';

/**
 * The totals line above the two tables. `errors` is refusals as well as
 * failures — a call the rate limit turned away is the one worth seeing — so it
 * is named "refused or failed" rather than "errors", which would read as ours.
 */
export function connectorTotal(usage: AdminUsage): string | null {
  const mcp = usage.mcp;
  if (!mcp || mcp.calls === 0) return null;
  const errors = mcp.errors === 0 ? '' : `, ${mcp.errors} of them refused or failed`;
  return `${mcp.calls} ${mcp.calls === 1 ? 'call' : 'calls'} this month${errors}.`;
}

/** One tool's line: how often, how often it did not answer, and how long it usually took. */
export interface ConnectorToolRow {
  readonly key: string;
  readonly tool: string;
  readonly calls: number;
  readonly errors: number;
  readonly median: string;
}

/**
 * Tool names are shown **verbatim**, the way model ids are: `search_knowledge`
 * is what the model called and what a log will say, and a friendly rewrite would
 * make the page and the log disagree about what happened.
 */
export function toolRows(usage: AdminUsage): ConnectorToolRow[] {
  return (usage.mcp?.by_tool ?? []).map((line) => ({
    key: line.tool,
    tool: line.tool,
    calls: line.calls,
    errors: line.errors,
    median: `${line.p50_ms} ms`,
  }));
}

/** One caller's line. Keyed by id, labelled by address — two people can share a name. */
export interface ConnectorUserRow {
  readonly key: string;
  readonly email: string;
  readonly calls: number;
}

export function userRows(usage: AdminUsage): ConnectorUserRow[] {
  return (usage.mcp?.by_user ?? []).map((line) => ({
    key: line.user_id,
    email: line.email,
    calls: line.calls,
  }));
}

/* ——————————————— the month's allowance ——————————————— */

/** The heading over the limit control, and the words the field is known by. */
export const MONTHLY_LIMIT_LABEL = 'Monthly spend limit';

/** What a blank field means, said under it. */
export const MONTHLY_LIMIT_NONE = 'No limit.';

/** What the field is for, under the control. */
export const MONTHLY_LIMIT_HELP =
  'The most this organization may spend on AI in a calendar month, across research, notes, ' +
  'identity matching and imports. It resets on its own when the month turns over.';

/** What to write when what is written is not an amount. */
export const MONTHLY_LIMIT_INVALID_NOTE =
  'Write an amount in dollars between 0.01 and 1,000,000, to the cent, or leave it blank for ' +
  'no limit.';

/**
 * **The unmistakable state.** Short, in the operator's words, and not hedged:
 * the organization is refused new work until this is raised or the month turns.
 */
export const MONTHLY_LIMIT_AT_THE_LIMIT = 'At the limit';

/** What being at it means, in one sentence under the flag. */
export const MONTHLY_LIMIT_AT_THE_LIMIT_DETAIL =
  'New research, imports and lookups are being refused for this organization. Raise the limit ' +
  'to let work start again; it clears on its own when the month turns over.';

/**
 * The month's spend against what it is allowed — the line the rollup leads with
 * once a limit exists.
 *
 * `null` when there is no limit, and the caller shows the plain total instead:
 * "of no limit" is not a sentence, and a total that is not being compared to
 * anything should not look as though it is.
 *
 * Both figures come off the usage read in micro-USD, so the comparison the
 * server enforces and the numbers on this line are the same two integers.
 */
export function monthlyLimitLine(usage: AdminUsage): string | null {
  const ceiling = usage.monthly_spend_ceiling_micro_usd;
  if (ceiling === null || ceiling === undefined) return null;
  return `Spend this month: ${formatMoney(usage.total_micro_usd)} of ${formatMoney(ceiling)}`;
}

/** Whether the panel shows the flag — the server's own answer, never a recomputation. */
export function atTheMonthlyLimit(usage: AdminUsage | null): boolean {
  return usage?.monthly_spend_ceiling_reached === true;
}
